import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams, Link, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { RESOURCES } from "@/lib/config";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import RecordDialog from "@/components/RecordDialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus } from "lucide-react";

export default function ResourceList({ resourceKey, subtitle, tabs, headerActions, actions }) {
  const cfg = RESOURCES[resourceKey];
  const { can } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0 });
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/${cfg.key}?page=${page}&page_size=25${q ? `&q=${encodeURIComponent(q)}` : ""}`);
      setData(res.data);
    } catch { toast.error("Could not load records."); }
    finally { setLoading(false); }
  }, [cfg.key, page, q]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (params.get("new") && can.write) setDialog({ mode: "create", record: {} });
  }, [params]); // eslint-disable-line

  const onRowClick = (row) => {
    if (cfg.detail) nav(`/${cfg.key}/${row.id}`);
    else setDialog({ mode: "edit", record: row });
  };

  const closeDialog = () => {
    setDialog(null);
    if (params.get("new")) { params.delete("new"); setParams(params, { replace: true }); }
  };

  return (
    <div className="p-6 animate-fade-in">
      <PageHeader title={cfg.title} subtitle={subtitle}>
        {headerActions && headerActions(load)}
        {can.write && (
          <Button data-testid={`create-${cfg.key}`} onClick={() => setDialog({ mode: "create", record: {} })}
            className="bg-navy hover:bg-atlantic text-white"><Plus className="h-4 w-4 mr-1.5" /> New {cfg.singular}</Button>
        )}
      </PageHeader>

      {tabs && (
        <div className="flex gap-1 mb-4 border-b border-border">
          {tabs.map((t) => (
            <Link key={t.to} to={t.to} data-testid={`tab-${t.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`px-4 py-2 text-sm nav-link -mb-px border-b-2 ${loc.pathname === t.to ? "border-atlantic text-navy font-medium" : "border-transparent text-mutedsea hover:text-slate"}`}>
              {t.label}
            </Link>
          ))}
        </div>
      )}

      <DataTable
        columns={cfg.columns} rows={data.items} total={data.total} page={page} pageSize={25}
        onPage={setPage} onSearch={(v) => { setQ(v); setPage(1); }} searchValue={q}
        onRowClick={onRowClick} loading={loading}
        actions={actions ? (row) => actions(row, load) : undefined}
      />

      {dialog && (
        <RecordDialog
          open={!!dialog} onOpenChange={(o) => !o && closeDialog()}
          fields={cfg.fields}
          title={dialog.mode === "create" ? `New ${cfg.singular}` : `Edit ${cfg.singular}`}
          initial={dialog.mode === "edit" ? dialog.record : {}}
          endpoint={dialog.mode === "create" ? `/${cfg.key}` : `/${cfg.key}/${dialog.record.id}`}
          method={dialog.mode === "create" ? "post" : "put"}
          onSaved={() => { closeDialog(); load(); }}
        />
      )}
    </div>
  );
}
